import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Save } from "lucide-react";

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    company_name: "",
    signer_name: "",
    buyer_legal_name: "",
    email: "",
    phone: "",
    mailing_address: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me/buyer-profile");
        setProfile((p) => ({ ...p, ...Object.fromEntries(Object.entries(data || {}).filter(([_, v]) => v != null)) }));
      } catch (e) {
        /* default */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me/buyer-profile", profile);
      toast.success("Buyer profile saved");
    } catch (e) {
      toast.error("Failed to save", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><span className="spinner" /></div>;
  }

  return (
    <div className="max-w-2xl" data-testid="profile-page">
      <div className="flex items-center gap-2 mb-2">
        <User size={18} style={{ color: "var(--lime)" }} />
        <div className="section-tag"><span className="dot" /> Buyer Profile</div>
      </div>
      <h1 className="h-display text-3xl sm:text-4xl mb-3">Your signing identity</h1>
      <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>
        This auto-fills the buyer section on every contract you generate. Fill it once and forget it. Don&apos;t have an LLC yet? Leave it blank and use your personal name as the signer.
      </p>

      <div className="surface-card p-6 space-y-4">
        <Field label="Company / LLC name (optional)" value={profile.company_name} onChange={(v) => update("company_name", v)} testid="company" placeholder="Leave blank if you don't have one yet" />
        <Field label="Buyer (entity) name as it appears on contract (optional)" value={profile.buyer_legal_name} onChange={(v) => update("buyer_legal_name", v)} testid="buyer-legal" placeholder="Defaults to company name, or your personal name if no LLC" />
        <Field label="Signer name (the person signing for the entity)" value={profile.signer_name} onChange={(v) => update("signer_name", v)} testid="signer" placeholder="e.g. Jane Doe" />
        <Field label="Email" value={profile.email} onChange={(v) => update("email", v)} testid="email" type="email" />
        <Field label="Phone" value={profile.phone} onChange={(v) => update("phone", v)} testid="phone" />
        <Field label="Mailing address" value={profile.mailing_address} onChange={(v) => update("mailing_address", v)} testid="mailing" textarea />

        <div className="pt-2">
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-profile-button">
            {saving ? <span className="spinner" /> : <Save size={14} />}
            {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, textarea }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {textarea ? (
        <textarea
          className="input-base"
          rows={2}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`profile-${testid}-input`}
        />
      ) : (
        <input
          className="input-base"
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`profile-${testid}-input`}
        />
      )}
    </div>
  );
}
